import React from "react"
import Layout from "../../../components/Layout"
import PageHero from "../../../components/PageHero"
import News from "../../../components/News"
import "../../../styles/news.scss"
import "../../../styles/caseStudy.scss"
import heroImg from "../../../images/news/Bigjerryfencing.jpg"
import clientLogo from "../../../images/clients/Colored/Logo-bigjerrysfencing.png"

const CHALLENGES = [
  {
    title: "Lead Leakage",
    desc: "Without a centralized system, many leads went unnoticed or untracked, causing missed opportunities.",
  },
  {
    title: "Slow & Inaccurate Quoting",
    desc: "Manual processes delayed quoting and led to mistakes, creating customer frustration and lost deals.",
  },
  {
    title: "Team Misalignment",
    desc: "Sales, operations, and installers often worked in silos, resulting in delays, rework, and extra costs.",
  },
  {
    title: "Customer Blind Spots",
    desc: "Clients had little visibility into project status, invoices, or documents, reducing trust and satisfaction.",
  },
]

const SOLUTIONS = [
  { num: "01", text: "CRM (Lead → Quote → Invoice)", desc: "Manage every lead, quote, and invoice in one centralized system — no more missed opportunities or scattered spreadsheets." },
  { num: "02", text: "Sketch Tool (Precision Planning)", desc: "A digital fence-planning tool that lets teams draw accurate site layouts and generate precise estimates instantly." },
  { num: "03", text: "Project Management (Odoo Projects)", desc: "Track every project, task, and deadline across crews and locations with full team visibility." },
  { num: "04", text: "Installer Mobile App", desc: "Field teams receive clear daily tasks and submit photo proof-of-work directly from the job site." },
  { num: "05", text: "Customer Portal (Transparency & Payments)", desc: "Clients get a self-service portal to track project status, view invoices, and make payments anytime." },
  { num: "06", text: "Dashboards & Reporting (Performance Insights)", desc: "Real-time dashboards give leadership instant visibility into sales performance, project health, and team productivity." },
]

const RESULTS = [
  { metric: "40%", label: "Faster turnaround from lead to completed installation" },
  { metric: "25%", label: "Higher quote-to-win ratio with Good / Better / Best quoting options" },
  { metric: "↓ Errors", label: "Significant error reduction in site planning thanks to the sketch tool" },
]

const RESULT_BULLETS = [
  "Improved installer productivity with clear tasks and mobile proof-of-work",
  "Happier customers with transparency via the self-service portal and faster communication",
  "Actionable business insights with real-time dashboards for leadership",
]

const BigJerrysFencing = () => (
  <div className="CaseStudeies">
    <Layout
      pageTitle="Visiomate - Big Jerry's Fencing Case Study"
      descrip="End-to-End Automation for Big Jerry's Fencing — CRM, Sketch Tool, Project Management, and Customer Portal built by Visiomate."
    >
      <PageHero
        eyebrow="Customer Story"
        title="Big Jerry's Fencing"
        description="End-to-end digital transformation across 21 cities in 9 U.S. states"
      />

      {/* About */}
      <section className="vm-news-section vm-news-section--light">
        <div className="vm-news-inner cs-intro">
          <div className="cs-intro__img">
            <img src={heroImg} alt="Big Jerry's Fencing" />
          </div>
          <div className="cs-intro__text">
            <img src={clientLogo} alt="Big Jerry's Fencing logo" className="cs-client-logo" />
            {/* <span className="vm-news-eyebrow vm-news-eyebrow--dark">Success Story</span> */}
            <h2 className="vm-news-heading vm-news-heading--dark">
              End-to-End Automation for Big Jerry's Fencing
            </h2>
            <p className="vm-news-sub vm-news-sub--dark">
              Big Jerry's Fencing is a trusted local fence company operating across 21 cities in nine
              U.S. states. They make buying fences fast, easy, and transparent — offering custom
              estimates, "Draw Your Fence" planning tools, diverse material and style options, and
              flexible financing.
            </p>
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="vm-news-section vm-news-section--gray">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">What They Were Up Against</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              Before working with Visiomate, Big Jerry's faced four critical operational gaps.
            </p>
          </div>
          <div className="cs-challenge-grid">
            {CHALLENGES.map(c => (
              <div key={c.title} className="cs-challenge-card">
                <div className="cs-challenge-card__icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <h3 className="cs-challenge-card__title">{c.title}</h3>
                <p className="cs-challenge-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="vm-news-section vm-news-section--light">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            {/* <span className="vm-news-eyebrow vm-news-eyebrow--dark">Solution</span> */}
            <h2 className="vm-news-heading vm-news-heading--dark">What We Built</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              We designed and implemented an end-to-end digital ecosystem tailored to Big Jerry's workflow.
            </p>
          </div>
          <div className="cs-solution-layout">
            <div className="cs-solutions-grid">
              {SOLUTIONS.map(s => (
                <div key={s.num} className="cs-solution-item">
                  <div className="cs-solution-item__badge">{s.num}</div>
                  <div>
                    <span className="cs-solution-item__text">{s.text}</span>
                    <p className="cs-solution-item__desc">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="cs-solution-img">
              <img src={heroImg} alt="Big Jerry's Fencing" />
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="vm-news-section vm-news-section--gray">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            {/* <span className="vm-news-eyebrow vm-news-eyebrow--dark">Results</span> */}
            <h2 className="vm-news-heading vm-news-heading--dark">A Dramatic Transformation</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              Big Jerry's Fencing saw measurable impact across every part of the business.
            </p>
          </div>
          <div className="cs-results-grid">
            {RESULTS.map(r => (
              <div key={r.metric} className="cs-result-card">
                <span className="cs-result-card__metric">{r.metric}</span>
                <p className="cs-result-card__label">{r.label}</p>
              </div>
            ))}
            {RESULT_BULLETS.map(b => (
              <div key={b} className="cs-result-card cs-result-card--bullet">
                <div className="cs-result-card__icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <p className="cs-result-card__label">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conclusion */}
      {/* <section className="vm-news-section vm-news-section--dark">
        <div className="vm-news-inner cs-conclusion">
          <h2 className="vm-news-heading">The Outcome</h2>
          <p className="vm-news-sub">
            By unifying their entire workflow into one seamless digital system, Big Jerry's Fencing
            achieved faster operations, greater transparency, and stronger customer satisfaction. What
            was once a fragmented, manual process is now a streamlined, data-driven workflow —
            positioning Big Jerry's Fencing as a modern, customer-centric service provider in a highly
            competitive industry.
          </p>
        </div>
      </section> */}

      <News isAbout={true} />
    </Layout>
  </div>
)

export default BigJerrysFencing
